"use client";

import type React from "react";
import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Send,
  Upload,
  X,
  FileImage,
  CheckCircle2,
  Loader2,
  ChevronsUpDown,
} from "lucide-react";
import { z } from "zod";
import { gradient } from "./utils/constants";
import { useUploadThing } from "@/lib/uploadthing";
import { countries } from "@/lib/CountryCodes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const defaultValues = {
  name: "",
  email: "",
  phone: "",
  description: "",
  service: "",
};

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine((val) => {
      const phoneRegex =
        /^[+]?[1-9][\d]{0,15}$|^[+]?[(]?[0-9]{3}[)]?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4,6}$|^[+]?[0-9]{1,4}[-.\s]?[(]?[0-9]{1,3}[)]?[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,9}$/;
      return phoneRegex.test(val.replace(/\s/g, ""));
    }, "Please enter a valid phone number"),
  service: z.string().min(1, "Please select a service"),
  description: z
    .string()
    .min(10, "Please provide more details about your project"),
});

type ContactForm = z.infer<typeof contactSchema>;

interface UploadedFile {
  name: string;
  url: string;
  size: number;
}

export const services = [
  "Photo Retouching",
  "Color Correction",
  "Background Removal",
  "Clipping Path",
  "Ghost Mannequin",
  "Compositing",
  "Shadow & Reflection",
];

export function Contact() {
  const [formData, setFormData] = useState<Partial<ContactForm>>(defaultValues);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ContactForm, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File upload states
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // OTP States
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpBlocked, setOtpBlocked] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null,
  );

  // Country Selector
  const [selectedCountry, setSelectedCountry] = useState("US"); // Safe SSR default
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Success Dialog
  const [showSuccess, setShowSuccess] = useState(false);

  // Set user's country on client only (fixes hydration error)
  useEffect(() => {
    const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    const countryCode = userLocale.split("-")[1] ?? "US";
    setSelectedCountry(countryCode);
  }, []);

  const selectedDialCode =
    countries.find((c) => c.code === selectedCountry)?.dialCode ?? "+1";

  const selectedFlag =
    countries.find((c) => c.code === selectedCountry)?.flag ?? "🇺🇸";

  const filtered = countries.filter(
    ({ name, dialCode }) =>
      name.toLowerCase().includes(search.toLowerCase()) ||
      dialCode.includes(search),
  );

  // Before unload warning
  useEffect(() => {
    if (!isUploading) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading]);

  // ── KEY CHANGE: pass client email as metadata so UploadThing
  //   can group files under that identifier in your dashboard ──
  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    const clientEmail = formData.email?.trim() || "unknown-client";

    await startUpload(selectedFiles, { clientEmail });
  };

  // OTP Cooldown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const handleSendOtp = async () => {
    if (!formData.email) return;
    setIsSendingOtp(true);
    setOtpError(null);
    setOtpBlocked(false);

    try {
      const res = await fetch("/api/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();

      if (res.status === 429 && data.blocked) {
        setOtpBlocked(true);
        setOtpError(data.error);
        return;
      }

      if (!res.ok) throw new Error(data.error);

      setOtpSent(true);
      setOtpCooldown(60);
      setAttemptsRemaining(data.attemptsRemaining);
    } catch (err: any) {
      setOtpError(err.message || "Failed to send code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsVerifyingOtp(true);
    setOtpError(null);
    try {
      const res = await fetch("/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: otpValue }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setEmailVerified(true);
      setOtpError(null);
    } catch (err: any) {
      setOtpError(err.message || "Verification failed.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const updateFormData = (field: keyof ContactForm, value: any) => {
    if (field === "email") {
      setEmailVerified(false);
      setOtpSent(false);
      setOtpValue("");
      setOtpError(null);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const { startUpload } = useUploadThing("sampleFileUploader", {
    onUploadBegin: () => {
      setIsUploading(true);
      setUploadProgress(0);
    },
    onUploadProgress: (progress: number) => setUploadProgress(progress),
    onClientUploadComplete: (res) => {
      const files = res.map((f) => ({
        name: f.name,
        url: f.ufsUrl,
        size: f.size,
      }));
      setUploadedFiles((prev) => [...prev, ...files]);
      setSelectedFiles([]);
      setUploadProgress(0);
      setIsUploading(false);
      setUploadError(null);
    },
    onUploadError: (error) => {
      setUploadError(error.message || "Upload failed. Please try again.");
      setIsUploading(false);
      setUploadProgress(0);
    },
  });

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const newFiles = Array.from(files).filter((file) => {
        return file.type.startsWith("image/") && file.size <= 128 * 1024 * 1024;
      });

      setSelectedFiles((prev) => [...prev, ...newFiles].slice(0, 10));
      setUploadError(null);

      if (newFiles.length > 0) {
        startUpload(newFiles, {
          clientEmail: formData.email?.trim() || "unknown",
        });
      }
    },
    [formData.email, startUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log(formData);
      const validatedData = contactSchema.parse(formData);

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...validatedData,
          phone: `${selectedDialCode} ${validatedData.phone}`,
          uploadedFiles: uploadedFiles.map((f) => ({
            name: f.name,
            url: f.url,
            size: f.size,
          })),
        }),
      });

      const res = await response.json();

      if (!response.ok) {
        alert(res?.error ?? "Something went wrong!");
        return;
      }

      // Reset form
      setFormData(defaultValues);
      setErrors({});
      setUploadedFiles([]);
      setSelectedFiles([]);
      setEmailVerified(false);
      setOtpSent(false);
      setShowSuccess(true);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof ContactForm, string>> = {};
        error.issues.forEach((issue) => {
          if (issue.path[0]) {
            newErrors[issue.path[0] as keyof ContactForm] = issue.message;
          }
        });
        setErrors(newErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white w-full">
      <div className="mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-6">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Your Free Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your images? Fill out the form below and
              we&apos;ll provide you with a detailed quote within 24 hours.
            </p>
          </div>
        </motion.div>

        <div className="w-full lg:w-max mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Project Details
                </CardTitle>
                <CardDescription>
                  {!emailVerified && (
                    <p className="text-xs text-amber-600 mt-1">
                      🔒 Verify your email below to unlock file uploads.
                    </p>
                  )}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-1">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="Your sweet name??"
                        value={formData.name || ""}
                        onChange={(e) => updateFormData("name", e.target.value)}
                        className={errors.name ? "border-red-500" : ""}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-1">
                      <Label htmlFor="email">Email Address *</Label>
                      {/* Email + OTP logic remains same */}
                      <div className="flex gap-2">
                        <Input
                          id="email"
                          type="email"
                          placeholder="Ex. example@xyz.com"
                          value={formData.email || ""}
                          onChange={(e) =>
                            updateFormData("email", e.target.value)
                          }
                          disabled={emailVerified}
                          className={`flex-1 ${errors.email ? "border-red-500" : ""} 
                            ${emailVerified ? "border-green-500 bg-green-50" : ""}`}
                        />
                        {/*{!emailVerified && !otpBlocked && (*/}
                        {/*  <Button*/}
                        {/*    type="button"*/}
                        {/*    variant="outline"*/}
                        {/*    disabled={*/}
                        {/*      !formData.email || isSendingOtp || otpCooldown > 0*/}
                        {/*    }*/}
                        {/*    className="cursor-pointer"*/}
                        {/*    onClick={handleSendOtp}*/}
                        {/*  >*/}
                        {/*    {isSendingOtp ? (*/}
                        {/*      <Loader2 className="h-4 w-4 animate-spin" />*/}
                        {/*    ) : otpCooldown > 0 ? (*/}
                        {/*      `Resend (${otpCooldown}s)`*/}
                        {/*    ) : otpSent ? (*/}
                        {/*      "Resend Code"*/}
                        {/*    ) : (*/}
                        {/*      "Verify Email"*/}
                        {/*    )}*/}
                        {/*  </Button>*/}
                        {/*)}*/}
                        {/*{emailVerified && (*/}
                        {/*  <div className="flex items-center gap-1 text-green-600">*/}
                        {/*    <CheckCircle2 className="h-5 w-5" />*/}
                        {/*    <span>Verified</span>*/}
                        {/*  </div>*/}
                        {/*)}*/}
                      </div>

                      {otpSent && !emailVerified && (
                        <div className="flex gap-2 mt-2">
                          <Input
                            placeholder="Enter 6-digit code"
                            value={otpValue}
                            onChange={(e) =>
                              setOtpValue(
                                e.target.value.replace(/\D/g, "").slice(0, 6),
                              )
                            }
                            maxLength={6}
                            className="tracking-widest text-center font-mono text-lg"
                          />
                          <Button
                            type="button"
                            disabled={otpValue.length < 6 || isVerifyingOtp}
                            className="cursor-pointer"
                            onClick={handleVerifyOtp}
                          >
                            {isVerifyingOtp ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Confirm"
                            )}
                          </Button>
                        </div>
                      )}

                      {otpError && !otpBlocked && (
                        <p className="text-red-500 text-sm">{otpError}</p>
                      )}
                      {errors.email && (
                        <p className="text-red-500 text-sm">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone & Service */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="flex gap-2">
                        <Popover open={open} onOpenChange={setOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="w-32 justify-between font-normal"
                            >
                              {selectedFlag} {selectedDialCode}
                              <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-0" align="start">
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder="Search country or code..."
                                value={search}
                                onValueChange={setSearch}
                              />
                              <CommandList className="max-h-60">
                                <CommandEmpty>No country found.</CommandEmpty>
                                <CommandGroup>
                                  {filtered.map(
                                    ({ code, dialCode, flag, name }) => (
                                      <CommandItem
                                        key={code}
                                        value={code}
                                        onSelect={() => {
                                          setSelectedCountry(code);
                                          setSearch("");
                                          setOpen(false);
                                        }}
                                      >
                                        {flag} {dialCode}{" "}
                                        <span className="text-gray-500 ml-1 text-xs">
                                          {name}
                                        </span>
                                      </CommandItem>
                                    ),
                                  )}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>

                        <Input
                          id="phone"
                          placeholder="555-123-4567"
                          value={formData.phone || ""}
                          onChange={(e) =>
                            updateFormData("phone", e.target.value)
                          }
                          className={errors.phone ? "border-red-500" : ""}
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-red-500 text-sm">{errors.phone}</p>
                      )}
                    </div>

                    {/* Service */}
                    <div className="space-y-1">
                      <Label htmlFor="service">Primary Service Needed *</Label>
                      <Select
                        value={formData.service ?? ""}
                        onValueChange={(value) =>
                          updateFormData("service", value)
                        }
                      >
                        <SelectTrigger
                          className={`w-full ${errors.service ? "border-red-500" : ""}`}
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.service && (
                        <p className="text-red-500 text-sm">{errors.service}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1">
                    <Label htmlFor="description">Project Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Please describe your project in detail..."
                      value={formData.description || ""}
                      onChange={(e) =>
                        updateFormData("description", e.target.value)
                      }
                      className={`${errors.description ? "border-red-500" : ""}`}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  {/* ── File Upload Section ── */}
                  {/*{emailVerified && (*/}
                  <div className="space-y-3">
                    <Label>Sample Files (Optional)</Label>

                    {/* Warn user to enter email first if not yet filled */}
                    {!formData.email && (
                      <p className="text-xs text-amber-600">
                        💡 Enter your email above first — files will be tagged
                        to your email for easy identification.
                      </p>
                    )}

                    {/* Drop Zone */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                        isDragging
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        document.getElementById("file-input")?.click()
                      }
                    >
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files)}
                      />
                      <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm font-medium text-gray-700">
                        Drag & drop images here, or{" "}
                        <span className="text-blue-600 underline">browse</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, TIFF, WebP — up to 128MB each, max 10 files
                      </p>
                    </div>

                    {/* Selected files (not yet uploaded) */}
                    {selectedFiles.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-600">
                          {isUploading
                            ? `Uploading ${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""}...`
                            : `Ready to upload (${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""})`}
                        </p>

                        {/* File list — simple, no individual bars */}
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm border border-gray-200"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isUploading ? (
                                <Loader2 className="h-4 w-4 text-blue-500 animate-spin shrink-0" />
                              ) : (
                                <FileImage className="h-4 w-4 text-gray-400 shrink-0" />
                              )}
                              <span className="truncate text-gray-700">
                                {file.name}
                              </span>
                              <span className="text-gray-400 shrink-0">
                                {formatFileSize(file.size)}
                              </span>
                            </div>

                            {/* Hide remove button while uploading */}
                            {!isUploading && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSelectedFile(index);
                                }}
                                className="ml-2 text-gray-400 hover:text-red-500 shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}

                        {/* Overall progress bar — only shown while uploading */}
                        {isUploading && (
                          <div className="space-y-1 pt-1">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Uploading...</span>
                              <span className="font-medium text-blue-600">
                                {uploadProgress}%
                              </span>
                            </div>
                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-400">
                              Please don't close or refresh the page while
                              uploading.
                            </p>
                          </div>
                        )}

                        <Button
                          type="button"
                          onClick={handleUpload}
                          disabled={isUploading || !emailVerified}
                          variant="outline"
                          className="w-full"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Uploading {uploadProgress}%...
                            </>
                          ) : (
                            <>
                              <Upload className="mr-2 h-4 w-4" />
                              Upload {selectedFiles.length} File
                              {selectedFiles.length > 1 ? "s" : ""}
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Upload error */}
                    {uploadError && (
                      <p className="text-red-500 text-sm">{uploadError}</p>
                    )}

                    {/* Successfully uploaded files */}
                    {uploadedFiles.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-green-700">
                          ✓ {uploadedFiles.length} file
                          {uploadedFiles.length > 1 ? "s" : ""} uploaded
                          successfully
                        </p>
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                              {/* Clickable link to preview the uploaded file */}
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="truncate text-blue-600 underline hover:text-blue-800"
                              >
                                {file.name}
                              </a>
                              <span className="text-gray-400 shrink-0">
                                {formatFileSize(file.size)}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(index)}
                              className="ml-2 text-gray-400 hover:text-red-500 shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/*)}*/}

                  {isUploading && (
                    <p className="text-sm text-amber-600 text-center">
                      ⏳ Please wait for your files to finish uploading before
                      submitting.
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting || isUploading}
                    className={`w-full ${gradient} text-white py-3 text-lg font-semibold cursor-pointer`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Get Free Quote <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Success Popup */}
                <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                        <CheckCircle2 className="h-10 w-10 text-green-600" />
                      </div>
                      <DialogTitle className="text-2xl text-center">
                        Thank You!
                      </DialogTitle>
                      <DialogDescription className="text-center text-base">
                        We&apos;ve received your request. Our team will get back
                        to you within 24 hours.
                      </DialogDescription>
                    </DialogHeader>
                    <Button
                      onClick={() => setShowSuccess(false)}
                      className={`w-full ${gradient}`}
                    >
                      Back to Home
                    </Button>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
